import { ReactNode, useCallback, useMemo } from 'react';
import Image from 'next/image';
import tw, { theme } from 'twin.macro';
import { ArrowsPointingOutIcon, UserGroupIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { atom, useAtomValue } from 'jotai';

import { BaseModal, ButtonV2, IconButton } from '@restworld/ui-ds';
import { customImageLoader, getAssetSrc, HeadphoneAltIcon, useResponsiveBreakPoints } from '@restworld/utils-common';

import { useV2Router } from '../../dashboard/hooks/use-v2-router';

export const isFreemiumUserJpPageModalOpenAtom = atom<boolean>(false);

function FreemiumUserJPPageModal() {
  const { t: translateCommon } = useTranslation('common');
  const { t } = useTranslation('job-positions-page');
  const router = useV2Router();
  const isOpen = useAtomValue(isFreemiumUserJpPageModalOpenAtom);
  const content = useModalContent();
  const { isDesktop } = useResponsiveBreakPoints();

  const handleClose = useCallback(() => router.push('/employer/homepage'), [router]);

  const footerEl = useMemo(
    () => (
      <div tw="flex justify-center w-full py-6 md:(px-4)">
        <ButtonV2 onClick={() => router.push('/employer/purchase')}>{t('find_out_more')}</ButtonV2>
      </div>
    ),
    [router, t]
  );

  return (
    <BaseModal
      onClose={handleClose}
      open={isOpen}
      noPadding
      hideHeader
      modalStyles={[
        tw`bg-neutral-700 bg-no-repeat bg-cover object-contain bg-[url('/utils-assets/rw-logo-white.png')] h-full md:(w-full px-0 py-0 max-w-[95%] max-h-[95%] h-[95vh])`,
      ]}
      footer={footerEl}
      footerStyles={[tw`bg-neutral-700`]}
    >
      <div tw="p-4 flex flex-col w-full h-full md:(justify-center)">
        <div tw="absolute top-4 right-4 hidden md:(block)">
          <IconButton Icon={XMarkIcon} ariaLabel={translateCommon('close')} variant="reversed" onClick={handleClose} />
        </div>
        <div className="max-w-[640px] mx-auto flex flex-col gap-8">
          <h2 tw="text-[1.875rem] font-bold text-purple-50 max-w-[500px] mx-auto text-center mt-6 font-praktikal-bold">
            {t('find_the_right_candidate_without_wasting_time')}
          </h2>
          <div tw="flex w-full flex-col gap-6 md:(flex-row justify-between)">
            {content.map((v) => (
              <ContentItem key={v.text} {...v} />
            ))}
          </div>
          <div tw="px-4 lg:(px-0)">
            <div tw="h-[150px] w-full relative mx-auto md:(w-[540px] h-[286px])">
              <Image
                alt=""
                src={getAssetSrc(
                  isDesktop ? '/utils-assets/jp-page-screenshot.png' : '/utils-assets/jp-page-screenshot-mobile.png'
                )}
                tw="object-top object-cover"
                fill
                loader={customImageLoader}
              />
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  );
}

export default FreemiumUserJPPageModal;

function ContentItem({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div tw="flex flex-col items-center md:(max-w-[198px]) gap-2">
      <span>{icon}</span>
      <span tw="text-purple-50 font-semibold text-lg text-center">{text}</span>
    </div>
  );
}

function useModalContent() {
  const { t } = useTranslation('job-positions-page');
  const iconClasses = 'text-purple-50 h-5 w-5 min-w-[20px] min-h-[20px]';

  const content = useMemo(
    () => [
      {
        icon: <HeadphoneAltIcon fill={theme`colors.purple.50`} className={iconClasses} />,
        text: t('dedicated_support_from_a_recruiter'),
      },
      {
        icon: <UserGroupIcon className={iconClasses} />,
        text: t('access_to_a_community_of_experienced_workers', { count: 200000 }),
      },
      {
        icon: <ArrowsPointingOutIcon className={iconClasses} />,
        text: t('sponsorship_of_your_ad_on_platforms', { count: 15 }),
      },
    ],
    [t]
  );

  return content;
}
